import { v4 as uuidv4 } from "uuid";
import { create } from "zustand";
import { Plan, PlanDetail, PlanInfo } from "../../../types/plan";
import config from "../../../utils/config";
import {
  callChatApiV2,
  ChatMessageV2,
  ChatSuccessResponseV2,
} from "../types/aiChatClientV2";
import { SearchCriteria } from "../types/search_engine";
import {
  aiSearchCallbackV2,
  highlightSearchResults,
} from "../utils/aiChatUtils";
import { clientSearchEngine } from "../utils/searchEngine";
import { useFieldConfigStore } from "./fieldConfigStore";

// Determine the API URL based on environment
const API_BASE_URL = config.ServerBackEndUrl;
const CHAT_API_URL = `${API_BASE_URL}${config.aiChatV2Endpoint}`;

export interface SpacesName {
  name: string;
  dbas: string[];
}

export interface AIChatV2 {
  messages: ChatMessageV2[];
  historyMessagesToSend: ChatMessageV2[];
  isLoading: boolean;
  isSearching: boolean;
  error: string | null;
  currentSearchCriteria: SearchCriteria | null;
  currentHighlight: string | null;
  clearCurrentHighlight: () => void;
  plansInfo: PlanInfo | null;
  distanceData: any | null;

  // Actions
  sendMessage: (
    prompt: string,
    spacesNames: SpacesName[],
    selectedPlan?: Plan,
    selectedPlanDetail?: PlanDetail,
    useGlobalMode?: boolean,
    selectedSpaces?: string[]
  ) => Promise<void>;

  clearError: () => void;
  internalCADViewerAICallback: (chatMessage: ChatMessageV2) => void;
  setPlansInfoAndDistanceData: (plansInfo: any, distanceData: any) => void;
  clearHistoryMessagesToSend: () => void;
}

export const useAIChatStoreV2 = create<AIChatV2>((set, get) => ({
  messages: [],
  historyMessagesToSend: [],
  isLoading: false,
  isSearching: false,
  error: null,
  currentSearchCriteria: null,
  currentHighlight: null,
  plansInfo: null,
  distanceData: null,

  setPlansInfoAndDistanceData: (plansInfo, distanceData) => {
    set({ plansInfo, distanceData });

    // Auto-initialize field configs if not already done
    const fieldConfigStore = useFieldConfigStore.getState();
    if (plansInfo?.spaces && !fieldConfigStore.isInitialized) {
      fieldConfigStore.analyzeAndInitializeFields(plansInfo.spaces);
    }
  },

  clearError: () => {
    set({ error: null, currentSearchCriteria: null });
  },

  internalCADViewerAICallback: (chatMessage: ChatMessageV2) => {
    const { currentHighlight } = get();

    if (!chatMessage.result) return;

    if (!chatMessage.result) return;

    if (currentHighlight !== chatMessage.uuid) {
      // Utiliser la fonction utilitaire pour faire le highlighting
      highlightSearchResults(chatMessage.result);
      set({ currentHighlight: chatMessage.uuid ?? null });
    } else {
      // Effacer les highlights existants
      highlightSearchResults({ targetPlanInfo: undefined, searchResults: [] });
      set({ currentHighlight: null });
    }
  },

  sendMessage: async (
    prompt: string,
    spacesNames: SpacesName[],
    selectedPlan?: Plan,
    selectedPlanDetail?: PlanDetail,
    useGlobalMode: boolean = false,
    selectedSpaces?: string[]
  ) => {
    if (!prompt.trim()) return;

    const { plansInfo, historyMessagesToSend, distanceData } = get();
    if (plansInfo) {
      clientSearchEngine.updateData(plansInfo.spaces, distanceData);
    }

    // Use formatted field configurations instead of raw available fields
    const fieldConfigurations =
      clientSearchEngine.getFieldConfigurationsForAI();
    const availableFields = clientSearchEngine.getAvailableFields();

    const metadata = {
      searchMode: useGlobalMode ? ("global" as const) : ("current" as const),
      selectedSpaces: selectedSpaces || [],
      planInfo: !useGlobalMode && selectedPlan && selectedPlanDetail ? {
        propertyName: selectedPlan.propertyName,
        planName: selectedPlanDetail.planName,
      } : undefined,
      timestamp: new Date().toISOString(),
    };

    const newUserMessage: ChatMessageV2 = {
      uuid: uuidv4(),
      role: "user",
      content: prompt,
      metadata,
    };

    // Keep current messages state to pass as history
    const historyToSend = historyMessagesToSend;

    // Optimistically update UI with user message
    set((state) => ({
      messages: [...state.messages, newUserMessage],
      historyMessagesToSend: [...state.historyMessagesToSend, newUserMessage],
      isLoading: true,
      error: null,
      currentSearchCriteria: null,
    }));

    try {
      console.log(
        "Calling API V2 at:",
        CHAT_API_URL,
        "with prompt:",
        prompt,
        "and history:",
        historyToSend.map((msg) => ({
          role: msg.role,
          content: msg.content,
        })),
        "field configurations:",
        fieldConfigurations
      );

      const requestUuid = uuidv4();
      const result: ChatSuccessResponseV2 = await callChatApiV2(
        CHAT_API_URL,
        prompt,
        historyToSend.map((msg) => ({
          uuid: undefined,
          role: msg.role,
          content: msg.content,
        })),
        spacesNames,
        fieldConfigurations // Send field configurations instead of raw field list
      );

      console.log("API V2 Response:", result);

      // Handle the search criteria response
      if (result && result.aiResponse.success) {
        // Success response - create message with search results
        const assistantMessage: ChatMessageV2 = {
          uuid: requestUuid,
          role: "assistant",
          content: result.aiResponse.message || "Search completed successfully",
          rawContent: result.rawResponse,
          result: {
            targetPlanInfo: undefined,
            searchResults: [],
          },
        };

        set((state) => ({
          messages: [...state.messages, assistantMessage],
          isLoading: false,
          isSearching: true,
          currentSearchCriteria: result.aiResponse,
        }));

        /* this is dummy code to simulate search results         */

        const searchResultWithTarget = clientSearchEngine.searchWithResult(
          result.aiResponse
        );

        console.log("Search result with target:", searchResultWithTarget);

        const searchResult = aiSearchCallbackV2(
          result.aiResponse,
          plansInfo,
          distanceData
        );

        console.log("Search result:", JSON.stringify(searchResult));

        // HOW CAN WE LINK THE SEARCH RESULTS TO THE MESSAGE?
        //return;

        // Create the final message WITH search results
        const finalAssistantMessage: ChatMessageV2 = {
          ...assistantMessage,
          //result: searchResultWithTarget,
          result: searchResult,
        };

        // Add the final message to the state
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.uuid === requestUuid ? finalAssistantMessage : msg
          ),
          isSearching: false,
        }));

        // Apply highlighting
        setTimeout(() => {
          get().internalCADViewerAICallback(finalAssistantMessage);
        }, 200);

        console.log(
          "Simulated search results added to message:",
          finalAssistantMessage.result
        );
      } else {
        // Error response or no success
        const errorMessage =
          result.aiResponse.message || "Search could not be processed";

        const errorAssistantMessage: ChatMessageV2 = {
          uuid: requestUuid,
          role: "assistant",
          content: errorMessage,
          rawContent: result.rawResponse,
        };

        set((state) => ({
          messages: [...state.messages, errorAssistantMessage],
          isLoading: false,
          isSearching: false,
        }));
      }
    } catch (err) {
      console.error("Chat API V2 Error in store:", err);
      const errorMessage =
        err instanceof Error ? err.message : "An unknown error occurred.";

      // Create an error message to display in chat
      const errorAssistantMessage: ChatMessageV2 = {
        uuid: uuidv4(),
        role: "assistant",
        content: `Sorry, I encountered an error: ${errorMessage}`,
      };

      set((state) => ({
        messages: [...state.messages, errorAssistantMessage],
        isLoading: false,
        isSearching: false,
        error: errorMessage,
      }));
    }
  },

  clearHistoryMessagesToSend: () => {
    set({ historyMessagesToSend: [], messages: [] });
  },

  clearCurrentHighlight: () => {
    set({ currentHighlight: null });
  },
}));
